

























import { Vue, Component, Prop } from 'vue-property-decorator';
import { TranslateResult } from 'vue-i18n';
import { lazyInject } from '@/app/container';
import {
  SectionProgressInterface,
  SectionProgressSymbol,
  SectionCompletion
} from '@/app/statistics/section/section-progress';
import { EventStream, EventStreamSymbol } from '@/app/event-stream/event-stream';
import {
  getTextFragmentFromContent,
  getHtmlFragmentFromContent,
  queryElements,
  listenScroll,
  calculateSectionsCount,
  defaultCourseImageUri
} from '@/app/shared/utils/content-utils';
import { IntroLink, TocItem } from '@/app/shared/models';
import { humanReadableTime } from '@/app/shared/utils/date-time-utils';
import { initTermHints } from '@/app/shared/utils/term-utils';
import { setFocus } from '@/app/accessibility/accessibility-utils';
import aspectStoreModule from '@/app/aspect/apsect-state';
import VirtualMediaComponent from '@/app/presenter/components/virtual-media-component.vue';

@Component({
  components: {
    VirtualMediaComponent,
  }
})
export default class IntroComponent extends Vue {
  @Prop()
  public page: string;

  @Prop()
  public content: string;

  @Prop()
  public currentSection: TocItem|null;

  public sectionsCount: TranslateResult|null = null;
  public time: string|null = null;
  public sectionIndex: string|null = null;
  public coverTitle: string|null = null;
  public intro: string|null = null;
  public tocTitle: string|null = null;
  public tocLinks: IntroLink[] = [];
  public toc: string|null = null;

  @lazyInject(SectionProgressSymbol)
  private sectionProgressService: SectionProgressInterface;

  @lazyInject(EventStreamSymbol)
  private eventStream: EventStream;

  public get sectionImageUri(): string {
    let uri: string = '';
    if (aspectStoreModule.selectedChapter !== null && aspectStoreModule.selectedChapter.imageUri) {
      uri = aspectStoreModule.selectedChapter.imageUri;
    } else if (aspectStoreModule.selectedCourseAspectInstance !== null
      && aspectStoreModule.selectedCourseAspectInstance.sectionImageUri !== null) {
      uri = aspectStoreModule.selectedCourseAspectInstance.sectionImageUri;
    } else {
      uri = defaultCourseImageUri();
    }

    return uri;
  }

  public async mounted(): Promise<void> {
    await this.processContent();

    $(() => {
      setTimeout(async () => {
        if (this.currentSection !== null) {
          await this.sectionProgressService.findOrCreate(this.currentSection.id);
          listenScroll(this.page, this.currentSection.id, this.eventStream);
        }

        initTermHints();

      }, 200);

      this.setFocus();
    });
  }

  public sectionLinkId(index: number): string {
    return `section-link-${index}`;
  }

  private async processContent(): Promise<void> {
    const parsedContent: any = $.parseHTML(this.content);
    const $parsedContent = $(parsedContent);
    await this.setValues($parsedContent);
  }

  public get mediaOnPage(): boolean {
    // REVIEW(szazo): it is the exact copy of page component's, but needed to bring here too
    return $('video, audio', $(this.intro || '')).length > 0;
  }

  private async setValues($parsedContent: any): Promise<void> {
    if (aspectStoreModule.selectedChapter !== null && aspectStoreModule.selectedChapter.children) {
      const count = calculateSectionsCount(aspectStoreModule.selectedChapter.children)
      if (count > 0) {
        this.sectionsCount = this.$tc('section_intro.subsection_count', count, { count: count });
      } else {
        this.sectionsCount = ''
      }
    }
    const timeFromContent: string = getTextFragmentFromContent('.meta__item.time', $parsedContent);
    if (timeFromContent !== '') {
      this.time = humanReadableTime(timeFromContent);
    }
    this.sectionIndex = getTextFragmentFromContent('.intro__index', $parsedContent);
    const coverSelector = $('.intro-cover__title', $parsedContent).length > 0
      ? '.intro-cover__title'
      : '.intro__title';
    this.coverTitle = getTextFragmentFromContent(coverSelector, $parsedContent);
    this.intro = getHtmlFragmentFromContent('.intro__text', $parsedContent);
    this.toc = getHtmlFragmentFromContent('.section-toc.component', $parsedContent);
    this.tocTitle = getTextFragmentFromContent('.section-toc .section-toc__title', $parsedContent);
    this.tocLinks = await this.getTocLinks($parsedContent);
  }

  private async getTocLinks($parsedContent: any): Promise<IntroLink[]> {
    const $elements: any = queryElements('.section-toc.component ul li', $parsedContent);
    if ($elements === null) {
      return [];
    }

    let links: IntroLink[] = [];
    for (const element of $elements) {
      const $element = $(element);
      const $a: any = queryElements('a', $element);

      const href: string = $a.attr('href');
      const page: string = href.split('#')[0];
      let uri: string = href;
      let query: any = {};
      let percent: number = 0;
      if (href.indexOf('#') > -1) {
        const items: string[] = uri.split('#');
        uri = items[0];
        const id: string = items[1];
        query = { id };

        const completion = this.sectionProgressService.find(id);
        if (completion !== null) {
          percent = Math.round(completion.progress.completion);
        }
      }

      links = [...links, {
        uri,
        query,
        title:  $a.text(),
        percent
      }];
    }

    return links;
  }

  private setFocus(): void {
    setFocus('.section-title', -1);
  }
}
